import { useState, useEffect, useCallback } from 'react';
import { fetchPages, fetchPosts, fetchPost, fetchLandingPagesForPage, type Page, type PostGroup, type PostLanding } from './api';

// Route management hook
export function useRouter() {
  const [currentRoute, setCurrentRoute] = useState<
    | { type: 'home' }
    | { type: 'page'; slug: string }
    | { type: 'post'; slug: string }
  >(() => {
    const path = window.location.pathname.slice(1); // Remove leading /
    if (!path) return { type: 'home' as const };

    // For now, we'll determine if it's a page or post later when we have the pages data
    // This will be resolved in the App component
    return { type: 'page' as const, slug: path };
  });

  const [previousPage, setPreviousPage] = useState<{ type: 'page'; slug: string } | null>(null);

  const navigate = useCallback((type: 'page' | 'post', slug: string, fromPage?: { type: 'page'; slug: string }) => {
    const route = { type, slug } as const;

    // If navigating to a post, remember the previous page
    if (type === 'post' && fromPage) {
      setPreviousPage(fromPage);
    } else if (type === 'page') {
      setPreviousPage(null);
    }

    setCurrentRoute(route);

    // Use HTML5 History API instead of hash
    window.history.pushState({}, '', `/${slug}`);
  }, []);

  const navigateHome = useCallback((homePage: Page) => navigate('page', homePage.slug), [navigate]);

  const navigateBack = useCallback((fallbackPage: Page) => {
    if (previousPage) {
      navigate('page', previousPage.slug);
    } else {
      navigateHome(fallbackPage);
    }
  }, [previousPage, navigate, navigateHome]);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.slice(1);
      if (!path) {
        setCurrentRoute({ type: 'home' });
        return;
      }

      // We'll assume it's a page for now - this will be corrected by the App component
      setCurrentRoute({ type: 'page', slug: path });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { currentRoute, navigate, navigateHome, navigateBack, previousPage };
}

// Pages data hook
export function usePages(locale: string = 'fi') {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPages = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPages(locale);
        if (isMounted) setPages(data);
      } catch (err) {
        if (isMounted) setError(err instanceof Error ? err.message : 'Failed to load pages');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPages();
    return () => { isMounted = false; };
  }, [locale]);

  // Find home page: lowest navbarOrder (0 = home), or first page as fallback
  const homePage = pages.length > 0
    ? pages.reduce((prev, current) =>
      (prev.navbarOrder <= current.navbarOrder) ? prev : current
    )
    : null;

  return { pages, homePage, loading, error };
}

// Posts data hook
export function usePosts(pageSlug?: string, locale: string = 'fi') {
  const [posts, setPosts] = useState<PostGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pageSlug) {
      setPosts([]);
      return;
    }

    let isMounted = true;

    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPosts(pageSlug, locale);
        if (isMounted) setPosts(data);
      } catch (err) {
        if (isMounted) setError(err instanceof Error ? err.message : 'Failed to load posts');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPosts();
    return () => { isMounted = false; };
  }, [pageSlug, locale]);

  return { posts, loading, error };
}

// Single post hook
export function usePost(slug?: string, locale: string = 'fi') {
  const [post, setPost] = useState<PostGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setPost(null);
      return;
    }

    let isMounted = true;

    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPost(slug, locale);
        if (isMounted) {
          if (data.length > 0) {
            setPost(data[0]);
          } else {
            setError('Post not found');
          }
        }
      } catch (err) {
        if (isMounted) setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPost();
    return () => { isMounted = false; };
  }, [slug, locale]);

  return { post, loading, error };
}

// Landing pages for a specific page hook
export function useLandingPages(pageSlug?: string, locale: string = 'fi') {
  const [landingPages, setLandingPages] = useState<PostLanding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pageSlug) {
      setLandingPages([]);
      return;
    }

    let isMounted = true;

    const loadLandingPages = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchLandingPagesForPage(pageSlug, locale);
        if (isMounted) setLandingPages(data);
      } catch (err) {
        if (isMounted) setError(err instanceof Error ? err.message : 'Failed to load landing pages');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadLandingPages();
    return () => { isMounted = false; };
  }, [pageSlug, locale]);

  return { landingPages, loading, error };
}